import { Component, Input, OnInit } from '@angular/core';
import {
    AttachmentHandler,
    DisplayableMissionDto,
    OrganisationAuthorityService,
    PersonsOrganisationDto,
    SimpleAuthorityDto,
    SimpleAuthorityTypeDto,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import { FormatAuthorityPipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { Subject, takeUntil } from 'rxjs';

interface Authority {
    id: number;
    startDate: string;
    endDate: string;
    name: string;
    identifier: string;
    hasLibrary: boolean;
    canView: boolean;
    attachmentHandler: AttachmentHandler;
    authorityType: SimpleAuthorityTypeDto;
}

@Component({
    selector: 'authority-library',
    templateUrl: './authority-library.component.html'
})
export class AuthorityLibrary implements OnInit {
    @Input() organisationId: number;
    @Input() mission: DisplayableMissionDto;

    additionalWorkflowAuthorities: SimpleAuthorityDto[];
    additionalAuthorities: SimpleAuthorityDto[];
    authorities: Authority[];

    canViewOrganisation: boolean;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private organisationAuthorityService: OrganisationAuthorityService,
        private formatAuthorityPipe: FormatAuthorityPipe,
        private workspaceStateService: WorkspaceStateService
    ) {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                organisation =>
                    (this.canViewOrganisation = hasAnyPermission(
                        organisation.organisation,
                        PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
                    ))
            );
    }

    ngOnInit() {
        this.authorities = [];
        this.additionalWorkflowAuthorities =
            this.mission.missionWorkflowVersion.additionalAuthorities;
        this.additionalAuthorities = this.mission.additionalAuthorities;
        this.refreshAuthorities();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshAuthorities() {
        if (this.organisationId == null) {
            throw Error('Some error');
            return;
        }

        this.authorities = []
            .concat(this.findDelegatedAuthority())
            .concat(this.findAdditionalWorkflowAuthorities())
            .concat(this.findAdditionalMissionAuthorities());
    }

    private findDelegatedAuthority() {
        const { delegatedAuthority } = this.mission.missionWorkflowVersion;

        return delegatedAuthority != null
            ? [this.toAuthority(delegatedAuthority)]
            : [];
    }

    private findAdditionalWorkflowAuthorities() {
        const additionalAuthorities =
            this.mission?.missionWorkflowVersion?.additionalAuthorities ?? [];

        return additionalAuthorities.map(a => this.toAuthority(a));
    }

    private findAdditionalMissionAuthorities() {
        const additionalAuthorities = this.mission?.additionalAuthorities ?? [];

        return additionalAuthorities.map(a => this.toAuthority(a));
    }

    private toAuthority(authority: SimpleAuthorityDto) {
        return {
            id: authority.authorityType.id,
            name: this.formatAuthorityPipe.transform(authority, false, true),
            startDate: authority.startDate,
            endDate: authority.expiryDate,
            identifier: authority.identifier,
            hasLibrary: authority.hasLibrary,
            canView: authority.availableActions.canView,
            attachmentHandler:
                this.canViewOrganisation && authority.availableActions.canView
                    ? this.getAttachmentsHandler(authority.id)
                    : null,
            authorityType: authority.authorityType
        };
    }

    private getAttachmentsHandler(id: number) {
        const attachmentHandler: AttachmentHandler =
            this.organisationAuthorityService.attachmentHandler(
                id,
                this.organisationId
            );

        return attachmentHandler;
    }
}
